import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ConversationProvider,
  RendererProvider,
  SectionType,
  MessageUILayout,
  ChatLayout,
  PromptComposer,
  useSendMessage,
  DefaultMessageRenderer,
  SendMessageRequest,
  ChatFooterUI,
  useIsConversationLoading,
  MessageRenderer as MessageRendererType,
  ConversationProviderProps,
  MessageContent,
  BaseMessageContent,
  MessageSection,
  OnFetchConversationParams,
  Conversation,
} from '@rocketlane/rocket-llm';
import { MarkdownRenderer, ThinkingRenderer } from '@rocketlane/rocket-llm';
import { Button } from '@rocketlane/ui';
import { ChatAPI } from 'api/chat/chat-api';
import { API_HOST_AGENTS } from 'config/environment';
import { UserMetaProvider } from 'contexts/UserMetaContext';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { TextCitationRenderer } from './text-citation-renderer';
import './chat-pane.css';

const RightSidebar = styled.div`
  width: 400px;
  min-width: 400px;
  max-width: 400px;
  height: 100%;
  min-height: 0;
  background: var(--white-100);
  border-left: 1px solid var(--field-border-default);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/** Lets rocket-llm flex layout shrink so messages scroll inside the pane, not the page. */
const ChatBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--field-border-default);
  background: var(--white-100);
  position: relative;
  z-index: 10;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled(Button)`
  position: relative;
  z-index: 999;
  cursor: pointer;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: var(--scarlet-gray-50);
  }

  &:active {
    background-color: var(--scarlet-gray-100);
  }
`;

// Message section type for chat - using rocket-llm's MessageSection structure
type ChatMessageSection = MessageSection<
  MessageContent,
  BaseMessageContent,
  SectionType
>;

type ChatSidebarSource = {
  sourceId: number;
  sourceType: string;
  timestamp?: string;
};

interface ChatSidebarProps {
  isOpen: boolean;
  title?: string;
  accountId?: number;
  userId?: number;
  chatData?: {
    message: string;
    elementContext: string;
    sourceType: string;
    sourceId: number;
    sources?: ChatSidebarSource[];
  } | null;
  onChatDataUsed?: () => void;
  onClose: () => void;
}

interface ChatComponentProps extends Omit<
  ChatSidebarProps,
  'isOpen' | 'onClose' | 'title'
> {
  conversationId: string;
  initialMessageMetadataRef: React.MutableRefObject<Record<
    string,
    unknown
  > | null>;
}

const ChatComponent: React.FC<ChatComponentProps> = ({
  chatData = null,
  onChatDataUsed,
  conversationId,
  initialMessageMetadataRef,
}) => {
  const [hasSentInitialMessage, setHasSentInitialMessage] = useState(false);
  const hasSentRef = useRef(false); // Ref to ensure message is sent only once

  const isConversationLoading = useIsConversationLoading();
  const sendMessage = useSendMessage<ChatMessageSection>();

  // Reset hasSentInitialMessage and ref when chatData.message changes
  // This must run BEFORE the send effect to prevent race conditions
  useEffect(() => {
    if (chatData?.message) {
      setHasSentInitialMessage(false);
      hasSentRef.current = false; // Reset ref to allow sending again with new message
    }
  }, [chatData?.message]);

  // Send initial message - only after ConversationProvider is loaded and only once
  useEffect(() => {
    // Check if we should send the message
    const shouldSend =
      chatData?.message &&
      !hasSentInitialMessage &&
      !hasSentRef.current &&
      !isConversationLoading &&
      typeof sendMessage === 'function'; // Ensure sendMessage is available

    if (shouldSend) {
      // Add a small delay to ensure ConversationProvider is fully initialized
      const timer = setTimeout(() => {
        // Double-check conditions before sending
        if (hasSentRef.current || !chatData?.message) {
          return;
        }

        const messageContext: any = {};

        if (chatData?.elementContext) {
          messageContext.sectionPayload = chatData.elementContext;
        }
        if (chatData?.sourceType) {
          messageContext.sourceType = chatData.sourceType;
        }
        if (chatData?.sourceId) {
          messageContext.sourceId = chatData.sourceId.toString();
        }

        // Build message_metadata with collection_filters contract
        const getFilterKey = (sourceType: string) =>
          sourceType?.toLowerCase() === 'email'
            ? 'email_thread_id'
            : 'meeting_id';

        const collectionFilters: Array<{
          collection: string;
          filter: { in: Record<string, number[]> };
        }> = [];

        if (
          chatData?.sources &&
          Array.isArray(chatData.sources) &&
          chatData.sources.length > 0
        ) {
          // Segregate sources by sourceType (MEETING vs EMAIL)
          const bySourceType = chatData.sources.reduce<
            Record<string, number[]>
          >((acc, s) => {
            const sourceType = (s.sourceType || 'MEETING').toUpperCase();

            if (!acc[sourceType]) {
              acc[sourceType] = [];
            }
            acc[sourceType].push(s.sourceId);

            return acc;
          }, {});

          Object.entries(bySourceType).forEach(([sourceType, ids]) => {
            const key = getFilterKey(sourceType);

            collectionFilters.push({
              collection: sourceType,
              filter: { in: { [key]: ids } },
            });
          });
        } else if (chatData?.sourceType !== 'COMPANY_V2') {
          // Single source from sourceType & sourceId
          const sourceType = chatData?.sourceType || 'MEETING';
          const id = chatData?.sourceId;

          if (id != null) {
            const key = getFilterKey(sourceType);

            collectionFilters.push({
              collection: sourceType,
              filter: { in: { [key]: [id] } },
            });
          }
        }

        const messageMetadata: any = {
          collection_filters: collectionFilters,
        };

        // Store the metadata for use in subsequent messages
        initialMessageMetadataRef.current = messageMetadata;

        // Create two sections: one with sectionPayload, one with message
        const sections: ChatMessageSection[] = [];

        // First section: sectionPayload wrapped in <q></q>
        if (messageContext.sectionPayload) {
          sections.push({
            type: SectionType.TEXT,
            left_content: {
              type: 'text',
              value: `${messageContext.sectionPayload}`,
            } as unknown as MessageContent,
            right_content: null,
          } as unknown as ChatMessageSection);
        }

        // Second section: chatData.message
        sections.push({
          type: SectionType.TEXT,
          left_content: {
            type: 'text',
            value: chatData.message,
          } as unknown as MessageContent,
          right_content: null,
        } as unknown as ChatMessageSection);

        try {
          // Mark as sent immediately to prevent duplicate sends
          hasSentRef.current = true;
          setHasSentInitialMessage(true);

          sendMessage({
            messageSections: sections,
            messageMetaData: messageMetadata,
            conversationId,
          });

          onChatDataUsed?.();
        } catch {
          // Reset on error so it can retry
          hasSentRef.current = false;
          setHasSentInitialMessage(false);
          // Error will be handled by the ConversationProvider
        }
      }, 200); // Small delay to ensure provider is ready

      return () => {
        clearTimeout(timer);
      };
    }
  }, [
    hasSentInitialMessage,
    isConversationLoading,
    chatData?.message,
    chatData?.elementContext,
    chatData?.sourceId,
    chatData?.sourceType,
    chatData?.sources,
    onChatDataUsed,
    sendMessage,
    conversationId,
    initialMessageMetadataRef,
  ]);

  return (
    <ChatLayout>
      <MessageUILayout />
      <ChatFooterUI>
        <PromptComposer showAttachmentButton={false} />
      </ChatFooterUI>
    </ChatLayout>
  );
};

const messageRenderer: MessageRendererType = ({
  message,
  messageIndex,
  messages,
  children,
}) => {
  return (
    <DefaultMessageRenderer
      message={message}
      messageIndex={messageIndex}
      messages={messages}
    >
      {children}
    </DefaultMessageRenderer>
  );
};

const resolver = ({ section }: { section: any }) => {
  const Component = (
    [MarkdownRenderer, ThinkingRenderer, TextCitationRenderer] as any[]
  ).find((Resolver: any) => Resolver.canHandle(section))?.Component;

  return Component ? <Component section={section} /> : null;
};

type ChatWithProvidersProps = Omit<
  ChatSidebarProps,
  'isOpen' | 'onClose' | 'title'
>;
const ChatWithProviders: React.FC<ChatWithProvidersProps> = (props) => {
  // Generate a random UUID for conversation_id
  const [conversationId] = useState(() => uuidv4());
  // Store the initial message metadata to reuse for subsequent messages
  const initialMessageMetadataRef = useRef<Record<string, unknown> | null>(
    null,
  );

  const actions: ConversationProviderProps<ChatMessageSection>['actions'] =
    useMemo(
      () => ({
        onFetch: async ({
          conversationId: convId,
        }: OnFetchConversationParams) => {
          // Return empty conversation object for random/new conversation IDs
          // Since conversationId is randomly generated, there's no existing conversation to fetch
          return {
            conversation_id: convId,
            messages: [],
            metadata: {},
          } as Conversation<ChatMessageSection>;
        },
        onSend: async ({
          messageSections,
          messageMetaData,
          conversationId: convId,
        }: SendMessageRequest<ChatMessageSection>) => {
          // Extract messageContext from section metadata if messageMetaData is not provided
          let finalMessageMetadata = messageMetaData;

          if (!finalMessageMetadata && messageSections.length > 0) {
            const firstSection = messageSections[0] as any;

            if (firstSection?.metadata?.messageContext) {
              finalMessageMetadata = firstSection.metadata.messageContext;
            }
          }

          // If still no metadata, use the stored initial message metadata
          if (
            !finalMessageMetadata ||
            Object.keys(finalMessageMetadata).length === 0
          ) {
            finalMessageMetadata = initialMessageMetadataRef.current || {};
          }

          // Build the payload according to the required format
          const payload = {
            message: {
              sections: messageSections.map((section) => ({
                type: section.type,
                left_content: section.left_content,
                right_content: section.right_content,
              })),
              message_metadata: finalMessageMetadata,
            },
            conversation_metadata: {
              agent_name: 'signals_ask_ai',
            },
            conversation_id: convId,
            edit_mode: null,
            conversation_type: 'execution',
            target_message_id: 0,
          };

          return ChatAPI.sendMessageWithSections<ChatMessageSection>(payload);
        },
      }),
      [],
    );

  const { onChatDataUsed } = props;

  const streamingOptions = useMemo(
    () => ({
      getUrl: (streamId: string) => {
        return `${API_HOST_AGENTS}/api/v1/chat/stream/${streamId}`;
      },
      callbacks: {
        onStreamingComplete: () => {
          onChatDataUsed?.();
        },
      },
    }),
    [onChatDataUsed],
  );

  return (
    <ConversationProvider
      conversationId={conversationId}
      actions={actions}
      streamingOptions={streamingOptions}
    >
      <RendererProvider
        sectionRenderer={resolver as any}
        messageRenderer={messageRenderer}
      >
        <ChatComponent
          {...props}
          conversationId={conversationId}
          initialMessageMetadataRef={initialMessageMetadataRef}
        />
      </RendererProvider>
    </ConversationProvider>
  );
};

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  isOpen,
  ...props
}) => {
  const { title, onClose } = props;

  const handleClose = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    event?.preventDefault();
    window.dispatchEvent(new CustomEvent('closeInspector'));
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <UserMetaProvider>
      <RightSidebar className="chat-sidebar">
        <HeaderContent onClick={(e) => e.stopPropagation()}>
          <Title>{title}</Title>
          <CloseButton
            variant="ghost"
            leftIcon={<FontAwesomeIcon icon={faClose} size="xs" />}
            onClick={handleClose}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            aria-label="Close chat"
            title="Close chat"
          />
        </HeaderContent>
        <ChatBody>
          <ChatWithProviders {...props} />
        </ChatBody>
      </RightSidebar>
    </UserMetaProvider>
  );
};

type ChatSidebarContentProps = Omit<
  ChatSidebarProps,
  'isOpen' | 'onClose' | 'title'
>;

const ChatContentWithUserMeta: React.FC<ChatSidebarContentProps> = (props) => {
  return (
    <UserMetaProvider>
      <ChatBody>
        <ChatWithProviders {...props} />
      </ChatBody>
    </UserMetaProvider>
  );
};

export { ChatContentWithUserMeta as ChatSidebarContent };
